import { createHoldDetector } from './holdDetector';
import type { HandFrame, Point3 } from './types';

// Indices MediaPipe Hand Landmarker.
const WRIST = 0;
const THUMB_TIP = 4;
const INDEX_TIP = 8;
const MIDDLE_MCP = 9;

/** Tolérance autour du cadre : MediaPipe extrapole les points hors image. */
const FRAME_MARGIN = 0.02;
const MIN_HAND_SIZE = 0.02;

const inFrame = (p: Point3): boolean =>
  Number.isFinite(p.x) &&
  Number.isFinite(p.y) &&
  p.x >= -FRAME_MARGIN &&
  p.x <= 1 + FRAME_MARGIN &&
  p.y >= -FRAME_MARGIN &&
  p.y <= 1 + FRAME_MARGIN;

const dist2d = (a: Point3, b: Point3): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Distance pouce-index divisée par la taille de la main (poignet → base du majeur),
 * donc indépendante de la distance à la caméra. `null` si la main est
 * incomplète, hors champ ou dégénérée.
 */
export function pinchRatio(landmarks: readonly Point3[]): number | null {
  if (landmarks.length < 21) return null;
  const pts = [WRIST, THUMB_TIP, INDEX_TIP, MIDDLE_MCP].map((i) => landmarks[i] as Point3);
  if (!pts.every(inFrame)) return null;
  const [wrist, thumb, index, mcp] = pts as [Point3, Point3, Point3, Point3];
  const size = dist2d(wrist, mcp);
  if (size < MIN_HAND_SIZE) return null;
  return dist2d(thumb, index) / size;
}

export interface PinchEvent {
  readonly t: number;
}

export interface PinchDetectorOptions {
  /** Ratio sous lequel le pincement commence. */
  readonly enter: number;
  /** Ratio au-dessus duquel le pincement se relâche (hystérésis, `exit > enter`). */
  readonly exit: number;
  readonly minDurationMs: number;
  readonly maxGapMs: number;
}

export const DEFAULT_PINCH_OPTIONS: PinchDetectorOptions = {
  enter: 0.3,
  exit: 0.45,
  minDurationMs: 200,
  maxGapMs: 150,
};

export interface PinchDetector {
  push(t: number, landmarks: readonly Point3[] | null): PinchEvent | null;
  reset(): void;
}

export function createPinchDetector(options: Partial<PinchDetectorOptions> = {}): PinchDetector {
  const o = { ...DEFAULT_PINCH_OPTIONS, ...options };
  // Le détecteur générique se déclenche sur un signal HAUT : on lui donne -ratio.
  const hold = createHoldDetector({
    on: -o.enter,
    off: -o.exit,
    minDurationMs: o.minDurationMs,
    maxGapMs: o.maxGapMs,
  });
  return {
    push(t, landmarks) {
      const ratio = landmarks ? pinchRatio(landmarks) : null;
      const e = hold.push(t, ratio === null ? null : -ratio);
      return e && { t: e.t };
    },
    reset: () => {
      hold.reset();
    },
  };
}

export function detectPinches(
  frames: readonly HandFrame[],
  options: Partial<PinchDetectorOptions> = {},
): PinchEvent[] {
  const detector = createPinchDetector(options);
  return frames.flatMap((f) => detector.push(f.t, f.landmarks) ?? []);
}
